import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SEEN_KEY = "cbs_exit_offer_seen";

export default function ExitIntentOffer() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;

    const trigger = () => {
      if (sessionStorage.getItem(SEEN_KEY)) return;
      sessionStorage.setItem(SEEN_KEY, "1");
      setOpen(true);
    };

    // Don't arm anything until the visitor has actually spent time on the site,
    // so an early move toward the browser tabs doesn't fire it.
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 45000);

    // Desktop: only a genuine exit — mouse leaves off the TOP edge of the window
    // (heading for the tab/close/address bar), and only once armed.
    const onMouseOut = (e) => {
      if (armed && e.clientY <= 0 && !e.relatedTarget) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);

    // Touch devices have no "mouse leave", so fall back to a longer engaged-time
    // trigger there only. Desktop relies purely on real exit intent.
    const isTouch = typeof window !== "undefined" &&
      window.matchMedia && window.matchMedia("(hover: none)").matches;
    const mobileTimer = isTouch ? setTimeout(trigger, 75000) : null;

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(armTimer);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  const close = () => setOpen(false);

  const claim = async () => {
    const clean = email.trim();
    if (clean && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      return toast.error("Enter a valid email, or just continue.");
    }
    setBusy(true);
    try {
      if (clean) {
        await api.post("/leads", { email: clean, source: "exit_intent" });
      }
    } catch (_) {
      // non-blocking — still send them to claim their forms
    } finally {
      setBusy(false);
      setOpen(false);
      navigate("/forms");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 fade-up"
      data-testid="exit-intent-overlay"
      onClick={close}
    >
      <div
        className="relative bg-white max-w-md w-full border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-modal"
      >
        <button
          onClick={close}
          data-testid="exit-intent-close"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-primary text-white px-7 py-6">
          <Gift className="w-7 h-7" />
          <h2 className="font-heading font-black text-2xl mt-3 leading-tight">
            Before you go — grab any 3 forms, free.
          </h2>
          <p className="text-white/80 text-sm mt-2">
            No card needed. Pick any 3 dealership forms from our library and put them to work today.
          </p>
        </div>

        <div className="px-7 py-6">
          <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
            Where should we send your free picks? (optional)
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && claim()}
            placeholder="you@dealership.com"
            data-testid="exit-intent-email"
            className="w-full border border-border px-4 py-3 mt-2 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
          <button
            onClick={claim}
            disabled={busy}
            data-testid="exit-intent-claim"
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3.5 font-semibold mt-3 btn-lift disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
            Claim my 3 free forms
          </button>
          <button
            onClick={close}
            data-testid="exit-intent-dismiss"
            className="w-full text-center text-xs text-muted-foreground mt-3 hover:text-foreground"
          >
            No thanks, maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
